import { ReactElement, useCallback } from "react";
import Loading from "components/Loading";
import DisplayError from "components/info/DisplayError";
import { createFetchContext } from "components/context/FetchContextFactory";
import { fetchLeavesReview } from "../api";
import { LeavesReviewQuery, LeavesReviewResponse } from "../types";
import { AuthenticationError, AxiosResultDefaultError } from "api/request2";

interface LeavesLoaderProps {
  defaultQuery: LeavesReviewQuery;
  fetchImmediately?: boolean;
  children: (
    data: LeavesReviewResponse & { refresh: () => Promise<void> }
  ) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  LeavesReviewQuery,
  LeavesReviewResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function useLeavesReviewLeavesLoader() {
  return useFetch();
}

export default function LeavesReviewLeavesLoader({
  defaultQuery,
  fetchImmediately = false,
  children,
}: LeavesLoaderProps) {
  const request = useCallback(async (query: LeavesReviewQuery) => {
    return fetchLeavesReview(query);
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={defaultQuery}
      fetchImmediately={fetchImmediately}
    >
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) return <Loading />;
          if (error)
            return (
              <DisplayError typeError={error.code} retryAction={refresh} />
            );
          if (!data) return null;
          return children({ ...data, refresh });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
